import React, { FunctionComponent, ReactElement, useContext } from 'react';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';

import { DARK_BLACK } from 'constants/colours';
import Tooltip from 'components/common/other/Tooltip/Tooltip';
import UserContext from 'context/userContext';

import * as Styled from './DotDropdown.styles';

export interface DotDropdownItem {
  id: string;
  label: string | React.ReactNode;
  icon?: IconDefinition;
  svgIcon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  labelClassName?: string;
}

interface DotDropdownProps {
  dropdownItems: DotDropdownItem[];
  eventCard?: boolean;
  contentCard?: boolean;
  eventList?: boolean;
  insightsPage?: boolean;
}

const DotDropdown: FunctionComponent<React.PropsWithChildren<DotDropdownProps>> = ({
  dropdownItems,
  eventCard = false,
  eventList = false,
  contentCard = false,
  insightsPage = false
}) => {
  const user = useContext(UserContext);
  if (dropdownItems.length <= 0) {
    return null;
  }

  const renderDropDown = (): ReactElement[] => {
    return dropdownItems.map(
      ({ id, label, icon, svgIcon, onClick, disabled, labelClassName }, index) => {
        // Special handling for UNARCHIVE id with Outlook users on event lists
        if (id === 'UNARCHIVE' && user?.companyUsesOutlookCalendar && eventList) {
          if (disabled) {
            // If the item is specifically disabled (due to hasOutlookCalendarEvent being true)
            return (
              <Tooltip
                key={`dropdown-item-${index}-tooltiped-disabled`}
                toolTipId="disable-unarchive"
                text={
                  "Events can't be unarchived because Outlook Calendar can't restore deleted events"
                }
                ml={0}
                maxWidth="257px"
              >
                <Dropdown.Item
                  key={`dropdown-item-${index}-disabled`}
                  className="tw-cursor-not-allowed !tw-rounded-[4px] !tw-p-1 tw-opacity-40 hover:!tw-bg-light-blue active:!tw-text-black"
                  as="div"
                >
                  <div className="tw-flex">
                    {icon && <FontAwesomeIcon fixedWidth icon={icon} />}
                    {svgIcon && <Styled.IconWrapper>{svgIcon}</Styled.IconWrapper>}
                    <span className={labelClassName}>{label}</span>
                  </div>
                </Dropdown.Item>
              </Tooltip>
            );
          } else {
            // Render as a normal, enabled item if not disabled (can be restored/unarchived)
            return (
              <Dropdown.Item
                onClick={onClick} // Removed outer onClick because it's not needed for enabled items
                key={`dropdown-item-${index}`}
                className="!tw-rounded-[4px] !tw-p-1 hover:!tw-bg-light-blue active:!tw-text-black"
                // disabled prop will be false here, so it behaves as enabled
              >
                <div className="tw-flex">
                  {icon && <FontAwesomeIcon fixedWidth icon={icon} />}
                  {svgIcon && <Styled.IconWrapper>{svgIcon}</Styled.IconWrapper>}
                  <span className={labelClassName}>{label}</span>
                </div>
              </Dropdown.Item>
            );
          }
        }

        // Default rendering for all other items or non-Outlook UNARCHIVE cases
        const itemClasses = [
          '!tw-rounded-[4px]',
          '!tw-p-1',
          'hover:!tw-bg-light-blue',
          'active:!tw-text-black',
          disabled ? 'tw-cursor-not-allowed tw-opacity-40' : ''
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <Dropdown.Item
            onClick={onClick} // Removed outer onClick because it's not needed here either
            key={`dropdown-item-${index}`}
            className={itemClasses}
            disabled={disabled}
            as={disabled ? 'div' : undefined} // Keep this for general disabled items
          >
            <div className="tw-flex">
              {icon && <FontAwesomeIcon fixedWidth icon={icon} />}
              {svgIcon && <Styled.IconWrapper>{svgIcon}</Styled.IconWrapper>}
              <span className={labelClassName}>{label}</span>
            </div>
          </Dropdown.Item>
        );
      }
    );
  };
  return (
    <Styled.Dropdown drop={`down tw-z-100`}>
      <Styled.DropdownToggle
        data-test="dot-dropdown-toggle"
        data-testid="dot-dropdown-toggle"
        variant="white"
        className="no-caret border-0"
      >
        <FontAwesomeIcon fixedWidth icon={faEllipsisV} color={DARK_BLACK} />
      </Styled.DropdownToggle>
      <Dropdown.Menu
        className={`${eventCard && '!tw-ml-[-110px]'} ${contentCard && '!tw-ml-[-130px]'} ${
          insightsPage ? '!tw-mr-2 !tw-mt-2' : '!tw-mr-4'
        } !tw-mb-[2px]  !tw-min-w-fit !tw-border-none !tw-p-2 !tw-shadow-custom`}
      >
        {renderDropDown()}
      </Dropdown.Menu>
    </Styled.Dropdown>
  );
};

export default DotDropdown;
